import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Amplify } from 'aws-amplify';
import type { ResourcesConfig } from 'aws-amplify';
import AppRoutes from './routes/Routes';
import { awsConfig } from './aws-config/aws-config';
import { setConfigLoadFailed } from './store/slices/config/configSlice';
import { useLocation } from 'react-router-dom';
import IconIndicator from './components/user-feedback/IconIndicator';
import { globals } from './common/globals';
import { fetchConfig } from './store/slices/config/configThunks';
import { validateSession } from './store/slices/auth/authThunks';
import { cancelPendingAuth } from './store/slices/auth/authSlice';
import { selectIsAuthInProgress } from './store/slices/auth/authSelectors';
import { clearEmail } from './common/tokenPersistence';
import { selectConfigExpressSettings } from './store/slices/config/configSelectors';
import MaintainancePage from './pages/Maintenance';
import useDocumentTitle from './hooks/useDocumentTitle';
import { useFlowNavigationSync } from './hooks/useFlowNavigationSync';
import { useAppDispatch } from './store/store';

type AwsEnv = keyof typeof awsConfig;
const awsEnv = process.env.REACT_APP_ENVIRONMENT as AwsEnv;
const resolvedAwsConfig = awsConfig[awsEnv] as ResourcesConfig;

globals.awsConfig = resolvedAwsConfig;
Amplify.configure(resolvedAwsConfig);

const App = () => {
  const dispatch = useAppDispatch();
  globals.dispatch = dispatch;

  const location = useLocation();

  const lastPlaceRef = useRef('');
  const [loadingConfig, setLoadingConfig] = useState(true);

  const settings = useSelector(selectConfigExpressSettings);
  const isAuthInProgress = useSelector(selectIsAuthInProgress);

  useDocumentTitle();
  useFlowNavigationSync();

  useEffect(() => {
    dispatch(validateSession());
  }, [dispatch]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      if (isAuthInProgress) {
        dispatch(cancelPendingAuth());
        clearEmail();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [dispatch, isAuthInProgress]);

  useEffect(() => {
    const loadConfig = async () => {
      const cityIdentiferPath = location.pathname.split('/')[1];
      if (cityIdentiferPath === lastPlaceRef.current) {
        // Same place as the last load — this is an in-app step navigation
        // (e.g. apply-property -> apply-registrant), not a place change.
        // Config is already loaded; do not flip loadingConfig or this
        // unmounts and remounts the entire AppRoutes tree (and everything
        // under it) on every single step-to-step navigation.
        return;
      }

      lastPlaceRef.current = cityIdentiferPath;
      setLoadingConfig(true);
      await dispatch(setConfigLoadFailed(false));
      try {
        await dispatch(fetchConfig(cityIdentiferPath));
      } catch {
        await dispatch(setConfigLoadFailed(true));
      }
      setLoadingConfig(false);
    };
    loadConfig();
  }, [location.pathname, dispatch]);

  // Guard AppRoutes behind both the loadingConfig flag AND the presence of
  // settings. In React StrictMode effects fire twice: the second invocation
  // skips fetchConfig (lastPlaceRef already set) and calls setLoadingConfig(false)
  // before the first invocation's API call completes. Without the settings check
  // AppRoutes briefly renders with cityIdentifer=undefined, the wildcard route
  // matches, and "Page Not Found" flashes until the config arrives.
  const configReady = !loadingConfig && !!settings;

  return (
    <>
      {!configReady && (
        <div className="loading-container">
          <IconIndicator />
        </div>
      )}
      {configReady && (
        <>
          {!settings?.under_maintenance && <AppRoutes />}
          {settings?.under_maintenance && <MaintainancePage />}
        </>
      )}
    </>
  );
};

export default App;
